import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ClientAdministrationService } from './client-administration.service';
import { ClientDTO, CreateClientDTO, UpdateClientDTO, FilterDTO, FilterOperator, SortDirection } from '../../../base/models/models.swagger.generated';
import { ClientAdministrationFormType } from './client-administraion-edit-form/client-administration-edit-form';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { CustomValidatorsFactory } from '../../../base/shared/validators/custom-validators-factory';
import { AppErrorMessagesComponent } from '../../../base/error-messages/app-error-messages.component';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';
import { FilterMap } from '../../../base/shared/table/filter-map';
import { FilterMetadata } from 'primeng/api';
import { NgIf, NgClass } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { AuthService } from '../../../base/services/auth.service';



@Component({
  selector: 'app-client-administration',
  templateUrl: './client-administration.component.html',
  styleUrls: ['./client-administration.component.scss'],
  standalone: true,
  imports: [
    TableModule,
    ButtonModule,
    ToolbarModule,
    ToastModule,
    DialogModule,
    ReactiveFormsModule,
    InputTextModule,
    TranslateModule,
    AppErrorMessagesComponent,
    SearchFieldComponent,
    NgIf,
    NgClass,
    CheckboxModule,
    TagModule,

  ],
  providers: [ClientAdministrationService]
})
export class ClientAdministrationComponent implements OnInit, OnDestroy {
  public data: ClientDTO[] = [];
  public totalNoOfRecords: number = 0;
  public client: ClientDTO;
  public itemDialog: boolean = false;
  public pagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
  public form: FormGroup<ClientAdministrationFormType>;
  public globalFilters = ['name', 'address', 'zip', 'city', 'email', 'phoneNumber'];
  public columnFilters: { [key: string]: FilterMetadata | FilterMetadata[] } = {};
  private _subscription: Subscription = new Subscription();
  private searchText: string = '';
  public isAdmin = false;
  public isClientAdmin = false;

  constructor(
    private clientAdministrationService: ClientAdministrationService,
    private router: Router,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private modalService: ModalService,
    public authService: AuthService
  ) { }

  ngOnInit() {
    this.isAdmin = this.authService.isUserAdmin();
    this.isClientAdmin = this.authService.isUserClientAdmin();
    if (this.authService.isUserClientAdmin()) {
      this.loadMyClientOnly();
    } else {
      this.loadData();
    }
  }


  public get filterMatchModeOptions() {
    return FilterMap.getStringFilterMatchModeOptions();
  }
  private loadMyClientOnly() {
    this._subscription.add(
      this.clientAdministrationService.getMyClient().subscribe(client => {
        this.data = [client];
        this.totalNoOfRecords = 1;
        this.client = null;
        this.itemDialog = false;
      })
    );
  }


  private loadData() {
    this._subscription.add(
      this.clientAdministrationService.getData(this.pagedQuery).subscribe((data) => {
        this.data = data.items;
        this.totalNoOfRecords = data.totalNumberOfItems;
        this.client = null;
        this.itemDialog = false;
      })
    );
  }

  private getFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    const addFilter = (field: string, operator: FilterOperator, value: any) => {
      filters.push(new FilterDTO({ field, operator, value, isComplex: false }));
    };

    if (this.searchText) {
      addFilter('name', FilterOperator.Contains, this.searchText);
    }

    Object.entries(this.columnFilters).forEach(([key, filter]) => {
      if (Array.isArray(filter)) {
        filter.forEach(f => f.value && addFilter(key, FilterMap.getFilterOperator(f.matchMode), f.value));
      } else if (filter?.value) {
        addFilter(key, FilterMap.getFilterOperator(filter.matchMode), filter.value);
      }
    });
    return filters;
  }

  public onFilter(event: any) {
    this.columnFilters = event.filters;
    this.pagedQuery.filters = this.getFilters();
    this.loadData();
  }

  public openNew() {
    if (this.authService.isUserClientAdmin()) {
      return;
    }
    this.client = null;
    this.initForm();
    this.itemDialog = true;
  }

  public editItem(item: ClientDTO) {
    this.client = new ClientDTO(item);
    this.initForm();
    this.itemDialog = true;
  }

  public deleteClient(client: ClientDTO) {
    const dialog = this.modalService.showOkCancelDialog(
      'i18n.domain.client-administration.messages.delete-confirm-title',
      'i18n.domain.client-administration.messages.delete-confirm'
    );
    dialog.onOkSubject.subscribe(() => {
      this._subscription.add(
        this.clientAdministrationService.deleteRecord(client.id).subscribe(() => {
          this.loadData();
        })
      );
    });
  }




  public hideDialog() {
    this.itemDialog = false;
  }

  public saveClient() {
    if (this.authService.isUserClientAdmin() && !this.form.value.id) {
      return;
    }
    if (this.form.valid) {
      const clientData = this.form.value;
      if (clientData.id) {
        this._subscription.add(
          this.clientAdministrationService.updateRecord(clientData as UpdateClientDTO).subscribe(() => {
            this.loadData();
          })
        );
      } else {
        this._subscription.add(
          this.clientAdministrationService.addRecord(clientData as CreateClientDTO).subscribe(() => {
            this.loadData();
          })
        );
      }
    }
  }

  public onPageChange(event: any) {
    this.pagedQuery.skip = event.first;
    this.pagedQuery.take = event.rows;
    this.loadData();
  }

  public onSearchTextChanged(searchText: string) {
    this.searchText = searchText;
    const filters = this.getFilters();
    console.log('Search Text:', searchText);
    console.log('Generated Filters:', filters);
    this.pagedQuery.filters = filters;
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onSort(event: any) {
    this.pagedQuery.sortBy = event.field;
    this.pagedQuery.sortDirection = event.order === 1 ? SortDirection.Ascending : SortDirection.Descending;
    this.loadData();
  }

 private initForm() {
    const { id, name, address, zip, city, email, phoneNumber, isActive } = this.client || {};
    this.form = this.fb.group<ClientAdministrationFormType>({
      id: this.fb.control<number>(id || 0),
      name: this.fb.control<string>(name || '',[ Validators.required,  Validators.maxLength(50)]),
      address: this.fb.control<string>(address || '',[Validators.maxLength(100)]),
      zip: this.fb.control<string>(zip || '', [Validators.maxLength(10),CustomValidatorsFactory.createZipValidator()]),
      city: this.fb.control<string>(city || '', [Validators.maxLength(50)]),
      email: this.fb.control<string>(email || '',[ Validators.email,Validators.maxLength(100)]),
      phoneNumber: this.fb.control<string>(phoneNumber || '',  [Validators.maxLength(15),CustomValidatorsFactory.createPhoneValidator()]),
      isActive: this.fb.control<boolean>(isActive || true),
    });
  }

  public onPhoneInput(event: any): void {
    const input = event.target;
    const digitsOnly = (input.value || '').replace(/\D/g, '');

    
    if (digitsOnly.length > 12) {
      input.value = digitsOnly.substring(0, 12);
      this.form.get('phoneNumber')?.setValue(input.value);
    }
  }


  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}


